import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  gradesService,
  productsService,
  brandsService,
  familiesService,
  categoriesService,
} from '../services/api';

const GradeEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNewGrade = !id;

  const [loading, setLoading] = useState(!isNewGrade);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Resources
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [families, setFamilies] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    xp: '',
    description: '',
    icon: '',
    color: '#8BC34A',
    xpToNextLevel: '',
    pointsReward: 0,
    xpBonus: 0,
    order: 0,
    isActive: true,
  });

  const [benefits, setBenefits] = useState([]);
  const [benefitModalOpen, setBenefitModalOpen] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [productsRes, brandsRes, familiesRes, categoriesRes] = await Promise.all([
        productsService.getAll(),
        brandsService.getAll(),
        familiesService.getAll(),
        categoriesService.getAll(),
      ]);

      setProducts(productsRes.data);
      setBrands(brandsRes.data);
      setFamilies(familiesRes.data);
      setCategories(categoriesRes.data);

      if (!isNewGrade) {
        const gradeRes = await gradesService.getById(id);
        const grade = gradeRes.data;

        setFormData({
          name: grade.name || '',
          level: grade.level || '',
          xp: grade.xp || '',
          description: grade.description || '',
          icon: grade.icon || '',
          color: grade.color || '#8BC34A',
          xpToNextLevel: grade.xpToNextLevel || '',
          pointsReward: grade.pointsReward || 0,
          xpBonus: grade.xpBonus || 0,
          order: grade.order || 0,
          isActive: grade.isActive !== undefined ? grade.isActive : true,
        });

        setBenefits(grade.benefits || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const dataToSend = {
        ...formData,
        level: parseInt(formData.level),
        xp: parseInt(formData.xp),
        xpToNextLevel: formData.xpToNextLevel ? parseInt(formData.xpToNextLevel) : null,
        pointsReward: parseInt(formData.pointsReward),
        xpBonus: parseInt(formData.xpBonus),
        order: parseInt(formData.order),
      };

      if (isNewGrade) {
        await gradesService.create(dataToSend);
      } else {
        await gradesService.update(id, dataToSend);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/grades');
      }, 1500);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde du grade');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBenefit = async (benefitId) => {
    if (window.confirm('Supprimer cet avantage ?')) {
      try {
        await gradesService.deleteBenefit(id, benefitId);
        fetchData();
      } catch (err) {
        setError('Erreur lors de la suppression de l\'avantage');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Accueil
        </Link>
        <Link component={RouterLink} to="/grades" underline="hover" color="inherit">
          Grades
        </Link>
        <Typography color="text.primary">
          {isNewGrade ? 'Nouveau grade' : 'Modifier le grade'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        {isNewGrade ? 'Créer un grade' : `Modifier ${formData.name}`}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Grade {isNewGrade ? 'créé' : 'modifié'} avec succès !
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Formulaire principal */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Typography variant="h6" gutterBottom>
                Informations du grade
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Nom du grade"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Niveau"
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Ordre"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Icône / Emoji"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="🏆"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="color"
                    label="Couleur"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label="Actif"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    XP et Progression
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="XP requis pour ce niveau"
                    value={formData.xp}
                    onChange={(e) => setFormData({ ...formData, xp: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XP pour niveau suivant"
                    value={formData.xpToNextLevel}
                    onChange={(e) => setFormData({ ...formData, xpToNextLevel: e.target.value })}
                    inputProps={{ min: 1 }}
                    helperText="Laissez vide pour le niveau max"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Récompenses
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Points de fidélité gagnés"
                    value={formData.pointsReward}
                    onChange={(e) => setFormData({ ...formData, pointsReward: e.target.value })}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">⭐</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Bonus XP permanent"
                    value={formData.xpBonus}
                    onChange={(e) => setFormData({ ...formData, xpBonus: e.target.value })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    helperText="Bonus XP appliqué sur tous les gains"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : isNewGrade ? 'Créer' : 'Enregistrer'}
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/grades')}>
                  Annuler
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>

        {/* Gestion des avantages */}
        {!isNewGrade && (
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Avantages
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingBenefit(null);
                    setBenefitModalOpen(true);
                  }}
                >
                  Ajouter
                </Button>
              </Box>

              {benefits.length === 0 ? (
                <Alert severity="info">
                  Aucun avantage configuré
                </Alert>
              ) : (
                <List>
                  {benefits.map((benefit) => (
                    <ListItem
                      key={benefit.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBenefit(benefit.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}
                    >
                      <ListItemIcon>
                        <Typography fontSize="1.5rem">{benefit.icon || '✨'}</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit.name}
                        secondary={benefit.description}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Modal d'ajout d'avantage */}
      <BenefitModal
        open={benefitModalOpen}
        onClose={() => setBenefitModalOpen(false)}
        gradeId={id}
        benefit={editingBenefit}
        products={products}
        brands={brands}
        families={families}
        categories={categories}
        onSave={() => {
          setBenefitModalOpen(false);
          fetchData();
        }}
      />
    </Box>
  );
};

// Composant modal pour les avantages
const BenefitModal = ({ open, onClose, gradeId, benefit, products, brands, families, categories, onSave }) => {
  const isEditing = !!benefit;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    benefitType: 'discount_all',
    name: '',
    description: '',
    discountValue: '',
    discountType: 'percentage',
    targetIds: [],
    freeProductId: null,
    icon: '',
    color: '',
    isActive: true,
  });

  useEffect(() => {
    if (open) {
      if (benefit) {
        setFormData({
          benefitType: benefit.benefitType || 'discount_all',
          name: benefit.name || '',
          description: benefit.description || '',
          discountValue: benefit.discountValue || '',
          discountType: benefit.discountType || 'percentage',
          targetIds: benefit.targetIds || [],
          freeProductId: benefit.freeProductId || null,
          icon: benefit.icon || '',
          color: benefit.color || '',
          isActive: benefit.isActive !== undefined ? benefit.isActive : true,
        });
      } else {
        setFormData({
          benefitType: 'discount_all',
          name: '',
          description: '',
          discountValue: '',
          discountType: 'percentage',
          targetIds: [],
          freeProductId: null,
          icon: '',
          color: '',
          isActive: true,
        });
      }
    }
  }, [open, benefit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        ...formData,
        discountValue: formData.discountValue ? parseFloat(formData.discountValue) : null,
      };

      if (isEditing) {
        await gradesService.updateBenefit(gradeId, benefit.id, dataToSend);
      } else {
        await gradesService.addBenefit(gradeId, dataToSend);
      }

      onSave();
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const benefitTypes = [
    { value: 'discount_all', label: 'Réduction générale', needsDiscount: true },
    { value: 'discount_brand', label: 'Réduction marque', needsDiscount: true, needsTarget: 'brand' },
    { value: 'discount_family', label: 'Réduction famille', needsDiscount: true, needsTarget: 'family' },
    { value: 'discount_category', label: 'Réduction catégorie', needsDiscount: true, needsTarget: 'category' },
    { value: 'free_shipping', label: 'Livraison gratuite', needsDiscount: false },
    { value: 'double_points', label: 'Points doublés', needsDiscount: false },
    { value: 'triple_points', label: 'Points triplés', needsDiscount: false },
    { value: 'birthday_gift', label: 'Cadeau anniversaire', needsDiscount: false },
    { value: 'early_access', label: 'Accès anticipé', needsDiscount: false },
    { value: 'exclusive_products', label: 'Produits exclusifs', needsDiscount: false },
    { value: 'priority_support', label: 'Support prioritaire', needsDiscount: false },
    { value: 'free_product', label: 'Produit offert', needsDiscount: false, needsProduct: true },
    { value: 'custom', label: 'Personnalisé', needsDiscount: false },
  ];

  const selectedType = benefitTypes.find((t) => t.value === formData.benefitType);

  const getTargetOptions = () => {
    if (!selectedType?.needsTarget) return [];
    switch (selectedType.needsTarget) {
      case 'brand':
        return brands;
      case 'family':
        return families;
      case 'category':
        return categories;
      default:
        return [];
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? 'Modifier l\'avantage' : 'Ajouter un avantage'}
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Type d'avantage</InputLabel>
                <Select
                  value={formData.benefitType}
                  label="Type d'avantage"
                  onChange={(e) => setFormData({ ...formData, benefitType: e.target.value })}
                >
                  {benefitTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nom de l'avantage"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            {selectedType?.needsDiscount && (
              <>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de réduction</InputLabel>
                    <Select
                      value={formData.discountType}
                      label="Type de réduction"
                      onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    >
                      <MenuItem value="percentage">Pourcentage</MenuItem>
                      <MenuItem value="fixed">Montant fixe</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Valeur"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {formData.discountType === 'percentage' ? '%' : '€'}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
              </>
            )}

            {selectedType?.needsTarget && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={getTargetOptions()}
                  getOptionLabel={(option) => option.name || option.title || ''}
                  value={getTargetOptions().filter((opt) => formData.targetIds.includes(opt.id))}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, targetIds: newValue.map((item) => item.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Sélectionner les ${selectedType.needsTarget}s`}
                    />
                  )}
                />
              </Grid>
            )}

            {selectedType?.needsProduct && (
              <Grid item xs={12}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option.name || ''}
                  value={products.find((p) => p.id === formData.freeProductId) || null}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, freeProductId: newValue?.id || null });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Produit offert" required />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Icône"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="✨"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="color"
                label="Couleur"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : isEditing ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GradeEdit;





















